import { authorizeNetProfileParamsMapper } from '../mappers/authorize-net-profile-params.mapper';
import { courseMapper } from '../mappers/course.mapper';
import { creditCardMapper } from '../mappers/credit-card.mapper';
import { managerRequestMapper } from '../mappers/group-admin-request.mapper';
import { orderPaymentCardMapper } from '../mappers/order-payment-card.mapper';
import { packageMapper } from '../mappers/package.mapper';
import { registrationStatsMapper } from '../mappers/registration-stats.mapper';
import { rememberedInformationMapper } from '../mappers/remembered-information.mapper';
import { resendPasswordsDtoMapper } from '../mappers/resend-passwords.mapper';
import { resetPasswordDataMapper } from '../mappers/reset-password-data.mapper';
import { userMapper } from '../mappers/user.mapper';
import { AuthorizeNetProfileParams } from '../models/authorize-net-profile';
import { AuthorizeNetProfile } from '../models/authorizenet-profile';
import { Course } from '../models/course';
import { OrderPaymentCardCreation } from '../models/order-payment-card';
import { BasePackage } from '../models/package';
import { RegistrationStats } from '../models/registration-stats';
import { RememberedInformation } from '../models/remembered-information';
import { ResendPasswords } from '../models/resend-passwords';
import { ResetPasswordData } from '../models/reset-password-data';
import {
  User, UserCreation, UserShortCreation, UsersUpdate,
} from '../models/user';
import { appUrlsConfig } from './app-urls.config';
import { axiosService, catchHttpError, catchHttpErrorWithValidationSupport } from './axios.service';

/**
 * Get courses.
 * @param {number} userId User id
 * @returns {Promise<Course[]>}
 */
async function getCourses(userId) {
  const response = await axiosService.get(appUrlsConfig.user.courses(userId));
  return response.data.map(course => courseMapper.fromDto(course));
}

/**
 * Update user info data.
 * @param {object} data Data.
 * @param {number} data.userId User ID of user needed to update info.
 * @param {boolean} data.isManager Whether current user is PFS admin or group admin.
 * @param {User} data.groupAdmin Group admin.
 * @param {UserCreation | UserShortCreation} data.updatedData Data to update.
 * @returns {Promise<User>}
 */
async function updateUserInfo({
  userId, updatedData, groupAdmin, isManager,
}) {
  const updatedDataDto = userMapper.toCreationDto(updatedData);
  const url = isManager ? appUrlsConfig.user.admin.updateUser(userId) : appUrlsConfig.user.student.updateUser(userId);
  return catchHttpErrorWithValidationSupport(userMapper.validationErrorFromDto, async () => {
    const response = await axiosService.put(url, {
      ...updatedDataDto,
      ...managerRequestMapper.toDto({ groupAdmin }),
      group_id: isManager ? updatedDataDto.group_id : undefined,
      username: isManager ? updatedDataDto.username : undefined,
    });
    return userMapper.fromDto(response.data);
  });
}

/**
 * Update multiple users info.
 * @param {UsersUpdate[]} data User id.
 * @returns {Promise<void>}
 */
async function updateUsers(data) {
  const updateDataDto = userMapper.toUsersUpdateDto(data);
  await axiosService.put(appUrlsConfig.user.admin.updateUsers, updateDataDto);
}

/**
 * Get registration stats.
 * @param {number} userId User id.
 * @returns {Promise<RegistrationStats>}
 */
async function getRegistrationStats(userId) {
  const response = await axiosService.get(appUrlsConfig.user.getRegistrationStats(userId));
  return registrationStatsMapper.fromDto(response.data);
}

/**
 * Get remembered information.
 * @param {number} userId User ID.
 * @param {number} packageId Package ID.
 * @returns {Promise<RememberedInformation>}
 */
async function getRememberedInformation(userId, packageId) {
  const response = await axiosService.get(appUrlsConfig.user.rememberedInformation(userId, packageId));
  return rememberedInformationMapper.fromDto(response.data);
}

/**
 * Deactivate account.
 * @param {number} userId User id.
 * @returns {Promise<void>}
 */
async function deactivateAccount(userId) {
  return catchHttpError(() => axiosService.put(appUrlsConfig.user.admin.deactivate(userId)));
}

/**
 * Activate account.
 * @param {number} userId User id.
 * @returns {Promise<void>}
 */
async function activateAccount(userId) {
  return catchHttpError(() => axiosService.put(appUrlsConfig.user.admin.activate(userId)));
}

/**
 * Reset password of current user.
 * @param {number} id User id.
 * @param {ResetPasswordData} data Reset password data.
 * @returns {Promise<void>}
 */
async function resetPassword(id, data) {
  return catchHttpErrorWithValidationSupport(resetPasswordDataMapper.validationErrorFromDto, async () => {
    const dataDto = resetPasswordDataMapper.toDto(data);
    await axiosService.post(appUrlsConfig.user.admin.resetPassword(id), dataDto);
  });
}

/**
 * Add a credit card.
 * @param {OrderPaymentCardCreation} data Credit card creation data.
 * @returns {Promise<void>}
 */
async function addCreditCard(data) {
  return catchHttpError(async () => {
    const dto = orderPaymentCardMapper.toCreationDto(data);
    await axiosService.post(appUrlsConfig.user.createCreditCard, { ...dto.card, group_admin_id: dto.group_admin_id });
  });
}
/**
 * Get a credit cards.
 * @param {AuthorizeNetProfileParams} params AuthorizeNet profile params.
 * @returns {Promise<AuthorizeNetProfile>}
 */
async function getAuthorizeNetProfile(params) {
  return catchHttpError(async () => {
    const paramsDto = authorizeNetProfileParamsMapper.toDto(params);
    const response = await axiosService.get(appUrlsConfig.user.getAuthorizeNetProfile, { params: paramsDto });
    return creditCardMapper.authorizeNetProfileFromDto(response.data);
  });
}

/**
 * Get user by id.
 * @param {number} id User id.
 * @returns {Promise<User>}
 */
async function getUserById(id) {
  const response = await axiosService.get(appUrlsConfig.user.admin.getUserById(id));
  return userMapper.fromDto(response.data);
}

/**
 * Resend passwords
 * @param {ResendPasswords} data Resend passwords data.
 * @returns {Promise<void>}
 */
async function resendPasswords(data) {
  return catchHttpError(async () => {
    const resendPasswordsDto = resendPasswordsDtoMapper.toDto(data);
    await axiosService.post(appUrlsConfig.user.admin.resendPasswords, resendPasswordsDto);
  });
}

/**
 * Get user recent purchase packages.
 * @returns {Promise<BasePackage[]>}
 */
async function getRecentPurchases(groupAdmin) {
  const response = await axiosService.get(appUrlsConfig.user.recentPurchases(groupAdmin.id));
  return response.data.map(item => packageMapper.basePackageFromDto(item));
}

/**
 * Update user reminder settings.
 * @param {object} settings Reminder settings.
 * @param {number} settings.certificateRenewal Certificate renewal reminder setting.
 * @param {number} settings.inPersonClass In-person Food Manager class reminder setting.
 * @param {number} settings.smsCertificateRenewal SMS certificate renewal reminder setting.
 * @returns {Promise<void>}
 */
async function updateReminderSettings(settings) {
  return catchHttpError(async () => {
    await axiosService.put(appUrlsConfig.user.updateReminderSettings, {
      certificate_renewal: settings.certificateRenewal,
      in_person_class: settings.inPersonClass,
      sms_certificate_renewal: settings.smsCertificateRenewal,
    });
  });
}

/**
 * Get the temporary Food Manager survey for the current user.
 * Errors are not caught here, so the caller can hide the survey card without an error toast.
 * @returns {Promise<{isEligible: boolean, surveyUrl: string | null}>}
 */
async function getFmSurvey() {
  const response = await axiosService.get(appUrlsConfig.user.fmSurvey);
  const { is_eligible: isEligible, survey_url: surveyUrl } = response.data.data;
  return { isEligible, surveyUrl };
}

/**
 * Get the dashboard announcement while it is active.
 * Errors are not caught here, so the caller can hide the announcement card without an error toast.
 * @returns {Promise<{title: string, body: string, updatedAt: string} | null>}
 */
async function getDashboardAnnouncement() {
  const response = await axiosService.get(appUrlsConfig.user.dashboardAnnouncement);
  const announcement = response.data.data;
  if (!announcement) {
    return null;
  }
  const { title, body, updated_at: updatedAt } = announcement;
  return { title, body, updatedAt };
}

export const userService = {
  getCourses,
  getFmSurvey,
  getDashboardAnnouncement,
  updateUserInfo,
  getRegistrationStats,
  resetPassword,
  addCreditCard,
  getAuthorizeNetProfile,
  getUserById,
  deactivateAccount,
  activateAccount,
  resendPasswords,
  updateUsers,
  getRememberedInformation,
  getRecentPurchases,
  updateReminderSettings,
};
